import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const TermsAndConditions = () => {
  return (
    <div data-testid="terms-page">
      <Header />

      <div className="pt-32 pb-20 bg-slate-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold mb-8"
            data-testid="back-home-link"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>

          <div className="bg-white rounded-3xl shadow-lg p-8 md:p-12">
            <h1 className="text-4xl font-black text-slate-900 mb-8">Terms and Conditions</h1>
            <p className="text-sm text-slate-500 mb-8">Last Updated: January 2026</p>

            <div className="space-y-8 text-slate-700">
              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Agreement to Terms</h2>
                <p className="leading-relaxed">
                  By accessing or using the services provided by Ashvya Digital Media ("we," "us," or "our"), you agree to be bound by these Terms and Conditions. If you disagree with any part of these terms, you may not access our services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Services Provided</h2>
                <p className="leading-relaxed mb-4">
                  Ashvya Digital Media provides digital marketing services including but not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Social Media Management and Marketing</li>
                  <li>Google Ads and Meta Ads Campaign Management</li>
                  <li>Lead Generation Services</li>
                  <li>Content Creation (Reels, Videos, Graphics)</li>
                  <li>Content Marketing and Copywriting</li>
                  <li>Website Design and Development</li>
                  <li>Business Development Consulting</li>
                  <li>SEO and Digital Strategy</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Client Responsibilities</h2>
                <p className="leading-relaxed mb-4">
                  As a client, you agree to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide accurate and complete information required for service delivery</li>
                  <li>Grant necessary access to accounts, platforms, and assets</li>
                  <li>Respond to queries and provide feedback in a timely manner</li>
                  <li>Make payments as per agreed terms and schedules</li>
                  <li>Comply with all applicable laws and platform policies</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Payment Terms</h2>
                <p className="leading-relaxed mb-4">
                  Payment terms will be specified in individual service agreements. Generally:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Invoices are due within the specified payment period</li>
                  <li>Late payments may incur additional charges</li>
                  <li>Services may be suspended for non-payment</li>
                  <li>Refund policies vary by service and will be outlined in the service agreement</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Intellectual Property</h2>
                <p className="leading-relaxed mb-4">
                  Upon full payment, clients receive ownership of deliverables created specifically for their project. However:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>We retain the right to showcase work in our portfolio</li>
                  <li>Third-party assets (stock photos, licensed tools) remain property of their respective owners</li>
                  <li>Our methodologies, strategies, and proprietary tools remain our intellectual property</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Service Guarantees and Limitations</h2>
                <p className="leading-relaxed mb-4">
                  While we strive for excellence:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>We cannot guarantee specific results (rankings, leads, sales) as outcomes depend on multiple factors</li>
                  <li>We are not responsible for changes in third-party platforms (Google, Facebook, etc.)</li>
                  <li>Campaign performance may vary based on market conditions, competition, and budget</li>
                  <li>We will work diligently to achieve agreed-upon objectives within scope</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Confidentiality</h2>
                <p className="leading-relaxed">
                  We treat all client information as confidential and will not disclose it to third parties without consent, except as required by law or for service delivery (e.g., ad platforms).
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Termination</h2>
                <p className="leading-relaxed mb-4">
                  Either party may terminate services with written notice as per the service agreement. Upon termination:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Client is responsible for payment of services rendered up to termination date</li>
                  <li>We will provide access to deliverables created and paid for</li>
                  <li>Access credentials and account management will be transferred back to the client</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Limitation of Liability</h2>
                <p className="leading-relaxed">
                  To the fullest extent permitted by law, Ashvya Digital Media shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from the use of our services. Our total liability shall not exceed the amount paid for services in the preceding three months.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Governing Law</h2>
                <p className="leading-relaxed">
                  These terms shall be governed by and construed in accordance with the laws of India. Any disputes shall be subject to the exclusive jurisdiction of courts in Ghaziabad, Uttar Pradesh.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Changes to Terms</h2>
                <p className="leading-relaxed">
                  We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting on our website. Continued use of our services constitutes acceptance of modified terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Contact Information</h2>
                <p className="leading-relaxed mb-4">
                  For questions about these Terms and Conditions, please contact us:
                </p>
                <div className="bg-slate-50 rounded-xl p-6">
                  <p className="font-semibold mb-2">Ashvya Digital Media</p>
                  <p>Modinagar, Ghaziabad, Uttar Pradesh</p>
                  <p>Email: ashvyadigitalmedia@gmail.com</p>
                  <p>Phone: 9068922387</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsAndConditions;